/* global setTimeout, clearTimeout */
/**
 * Idle behaviour of Bobo, the Happy mascot, as a framework-free state machine (port of the admin `Bobo/idleMachine.js`).
 *
 * After `idleMs` of quiet Bobo runs one cycle, in this order: waves → looks around → ponders → hops → dances →
 * turns his back → slowly sits down (old-man style) → sits → struggles back up → rests, and the cycle
 * repeats. Any user activity ("poke") lets the motion that is running finish before Bobo returns to rest; a sitting Bobo gets up first. Nothing
 * ever snaps: every action maps to a reanimated timeline in `Bobo.js`, so this module only decides
 * WHICH action is active and for how long.
 *
 * Timers are injectable so the machine can be driven by fake timers in tests.
 */

/** Quiet period before the first action. */
export const IDLE_THRESHOLD_MS = 5000;

/** One cycle. `action: null` is a rest gap. Durations match the timelines in `Bobo.js`. */
export const IDLE_SEQUENCE = Object.freeze([
    {
        action: 'wave',
        ms: 2600 
    },
    {
        action: null,
        ms: 1000 
    },
    {
        action: 'lookAround',
        ms: 2800 
    },
    {
        action: 'think',
        ms: 3000 
    },
    {
        action: 'hop',
        ms: 1500 
    },
    {
        action: null,
        ms: 900 
    },
    {
        action: 'dance',
        ms: 3400 
    },
    {
        action: 'turn',
        ms: 2400 
    },
    {
        action: 'sitDown',
        ms: 1400 
    },
    {
        action: 'sit',
        ms: 5000 
    },
    {
        action: 'standUp',
        ms: 1300 
    },
    {
        action: null,
        ms: 2500 
    },
]);

/**
 * Create the idle machine.
 *
 * @param {Object} options Options.
 * @param {function(string|null): void} options.onChange Called whenever the active action changes (`null` = rest).
 * @param {number} [options.idleMs] Quiet period before the first action.
 * @param {ReadonlyArray<{action: (string|null), ms: number}>} [options.sequence] Action timeline.
 * @param {Function} [options.setTimer] `setTimeout` replacement.
 * @param {Function} [options.clearTimer] `clearTimeout` replacement.
 * @returns {{ start: function(): void, stop: function(): void, poke: function(): void, getAction: function(): (string|null) }}
 *   Controls.
 */
export function createIdleMachine({
    onChange,
    idleMs = IDLE_THRESHOLD_MS,
    sequence = IDLE_SEQUENCE,
    setTimer = setTimeout,
    clearTimer = clearTimeout,
}) {
    const standUpIndex = sequence.findIndex((item) => item.action === 'standUp');
    let timer = null;
    let step = -1;
    let current = null;
    let finishing = false;
    let running = false;

    const emit = (action) => {
        if (action !== current) {
            current = action;
            onChange(action);
        }
    };

    const clear = () => {
        if (timer !== null) {
            clearTimer(timer);
            timer = null;
        }
    };

    let runStep = () => undefined;

    const wait = () => {
        clear();
        step = -1;
        finishing = false;
        emit(null);
        timer = setTimer(() => runStep(0), idleMs);
    };

    const advance = () => {
        const finished = sequence[step].action;

        if (finishing) {
            if (finished === 'sitDown' && standUpIndex >= 0) {
                runStep(standUpIndex);

                return;
            }
            wait();

            return;
        }
        runStep(step + 1);
    };

    runStep = (index) => {
        step = index % sequence.length;
        emit(sequence[step].action);
        timer = setTimer(advance, sequence[step].ms);
    };

    return {
        start() {
            if (!running) {
                running = true;
                wait();
            }
        },
        stop() {
            running = false;
            clear();
            step = -1;
            finishing = false;
            emit(null);
        },
        poke() {
            if (!running) {
                return;
            }
            if (step === -1 || current === null) {
                // Resting (or in a rest gap): nothing is moving, just restart the quiet countdown.
                wait();

                return;
            }
            if (current === 'sit' && standUpIndex >= 0) {
                clear();
                finishing = true;
                runStep(standUpIndex);

                return;
            }
            // A running motion (wave / think / turn / sitDown / standUp) finishes before Bobo rests.
            finishing = true;
        },
        getAction() {
            return current;
        },
    };
}

export default createIdleMachine;
